import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { JarvisCard } from './JarvisCard'
import { ALL_PERSONAS, usePersona, type Persona } from '@/lib/personas'
import { apiClient, unwrap } from '@/lib/apiClient'
import { HAS_AUTH } from '@/lib/auth'
import { cn } from '@/lib/cn'

export function PersonaPicker() {
  const { personaId, setPersona } = usePersona()
  const queryClient = useQueryClient()

  const persist = useMutation({
    mutationFn: (id: Persona) =>
      unwrap(apiClient.patch('/settings', { persona: id })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['user'] }),
  })

  const handle = (id: Persona) => {
    setPersona(id)
    if (HAS_AUTH) persist.mutate(id)
  }

  return (
    <JarvisCard className="space-y-4">
      <div>
        <h2 className="text-xs uppercase tracking-widest text-jarvis-muted">
          Persona
        </h2>
        <p className="text-sm text-jarvis-muted mt-1">
          Choose which AI persona runs your business. Same data — different look,
          different voice. You can change anytime.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        {ALL_PERSONAS.map((p) => {
          const Icon = p.Icon
          const isActive = p.id === personaId
          return (
            <button
              key={p.id}
              onClick={() => handle(p.id)}
              className={cn(
                'relative text-left p-4 rounded-xl border transition-all',
                'flex flex-col gap-3',
                isActive
                  ? 'border-jarvis-blue bg-jarvis-blue/5 ring-1 ring-jarvis-blue/40'
                  : 'border-jarvis-border bg-jarvis-surface-2 hover:border-jarvis-blue/40',
              )}
            >
              {isActive && (
                <span className="absolute top-3 right-3 w-5 h-5 rounded-full bg-jarvis-blue flex items-center justify-center">
                  <Check size={12} strokeWidth={3} className="text-white" />
                </span>
              )}
              <span
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{
                  background: `${p.swatch.primary}25`,
                  color: p.swatch.primary,
                }}
              >
                <Icon
                  size={20}
                  strokeWidth={2.5}
                  className={cn(p.id === 'symbiote' && 'rotate-180')}
                />
              </span>
              <div>
                <div className="font-bold tracking-widest text-sm">{p.wordmark}</div>
                <div className="text-xs text-jarvis-muted mt-0.5">{p.tagline}</div>
              </div>
              <p className="text-xs text-jarvis-muted leading-relaxed">
                {p.description}
              </p>
              <div className="flex gap-1.5 pt-1 border-t border-jarvis-border/40">
                <span
                  className="w-4 h-4 rounded-full"
                  style={{ background: p.swatch.primary }}
                />
                <span
                  className="w-4 h-4 rounded-full"
                  style={{ background: p.swatch.secondary }}
                />
                <span className="text-[10px] text-jarvis-muted ml-auto self-center italic">
                  {p.greeting}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </JarvisCard>
  )
}
